import { MouseEvent } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { PAGES } from 'shared/config/constants';
import {
  Assignment,
  AssignmentContent,
  Brand,
} from 'shared/api/assignment-service';
import {
  generateCardEventHandlers,
  getPage,
  XApiEventKeys,
} from 'shared/api/event-service';
import { dateService } from 'shared/lib/date';
import {
  getDomainProgramLink,
  getLessonLink,
  getNewWindowLinkAttrs,
  isDesmosAssignment,
  parseLesson,
} from 'shared/lib/utils';
import { ProgramGrades } from 'shared/lib/grades';
import { OvalButton } from 'shared/ui/buttons';
import { Breadcrumbs } from 'shared/ui/breadcrumbs';

import styles from './styles.module.scss';

export type ScheduledAssignmentCardProps = {
  assignment: Assignment;
  assignmentClassLabel: string;
  currentClassGrades: ProgramGrades[];
  onEditBtnClick?: () => void;
  onUnassignBtnClick?: () => void;
};

export const ScheduledAssignmentCard = ({
  assignment,
  assignmentClassLabel,
  currentClassGrades,
  onEditBtnClick,
  onUnassignBtnClick,
}: ScheduledAssignmentCardProps) => {
  const { t } = useTranslation();

  const content =
    assignment.type === 'Cardstack'
      ? assignment.content[0]
      : assignment.content;

  const domain =
    content.brand === Brand.CUSTOM
      ? 'Slides Custom Created From Scratch'
      : content.domain;

  const { formattedStartDate, localeStartDate } =
    dateService.getScheduledStartDate(assignment.startDate);
  const { formattedDueDate, localeDueDate } = dateService.getScheduledDueDate(
    assignment.dueDate,
  );

  const isDesmos = isDesmosAssignment(assignment);

  const desmosActivityLandingPageUrl = content.activityLandingPageURL;

  const [parsedLessonNumber, lessonName] = parseLesson(
    content.title ?? (content as AssignmentContent).lessonTitle,
    content.brand === Brand.REGULAR,
  );

  const lessonLink = isDesmos
    ? desmosActivityLandingPageUrl
    : getLessonLink(content);
  const lessonTitle = content.title ?? '';
  const lessonNumber = isDesmos ? lessonTitle : parsedLessonNumber;

  const domainProgramLink = getDomainProgramLink(assignment);

  const isTutorial =
    content.brand !== Brand.CUSTOM && content.domain === 'Tutorial';

  const {
    onProductBreadcrumbClick,
    onUnitBreadcrumbClick,
    onLessonBreadcrumbClick,
    onAssignmentActionsClick,
  } = generateCardEventHandlers({
    currentClassGrades,
    item: assignment,
  });

  const handleDomainBadgeClick = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    await onProductBreadcrumbClick({
      isRightClick: e.type === 'contextmenu',
      lessonStatus: 'scheduled',
      url: window.location.href,
      page: getPage(),
      section: getPage() === PAGES.SCHEDULED ? undefined : PAGES.SCHEDULED,
    });
  };

  const handleBreadcrumbClick = async (
    e: MouseEvent<HTMLAnchorElement>,
    locationUrl?: string,
    isUnit?: boolean,
  ) => {
    if (e.type !== 'contextmenu') {
      e.preventDefault();
    }

    if (isUnit) {
      await onUnitBreadcrumbClick({
        isRightClick: e.type === 'contextmenu',
        lessonStatus: 'scheduled',
        url: window.location.href,
        page: getPage(),
        section: getPage() === PAGES.SCHEDULED ? undefined : PAGES.SCHEDULED,
      });
    } else {
      await onLessonBreadcrumbClick({
        isRightClick: e.type === 'contextmenu',
        lessonStatus: 'scheduled',
        url: window.location.href,
        page: getPage(),
        section: getPage() === PAGES.SCHEDULED ? undefined : PAGES.SCHEDULED,
      });
    }

    if (e.type !== 'contextmenu') {
      const redirectParams = getNewWindowLinkAttrs(content.brand);
      const target = redirectParams.target ? redirectParams.target : '_self';
      const features = redirectParams.rel ? 'noreferrer,noopener' : undefined;

      window.open(locationUrl, target, features);
    }
  };

  const handleCardBtnXApiEvents = (
    eventType: XApiEventKeys,
    isRightClick = false,
  ) => {
    onAssignmentActionsClick({
      eventType,
      assignment,
      isRightClick,
      url: window.location.href,
      page: getPage(),
      section: getPage() !== PAGES.SCHEDULED ? 'scheduled' : undefined,
      lessonStatus: 'scheduled',
    });
  };

  const handleEditClick = () => {
    handleCardBtnXApiEvents(XApiEventKeys.assignmentEditSelected);
    onEditBtnClick?.();
  };

  const handleEditContextMenu = () => {
    handleCardBtnXApiEvents(XApiEventKeys.assignmentEditSelected, true);
  };

  const handleUnassignClick = () => {
    handleCardBtnXApiEvents(XApiEventKeys.assignmentUnassignSelected);
    onUnassignBtnClick?.();
  };

  const handleUnassignContextMenu = () => {
    handleCardBtnXApiEvents(XApiEventKeys.assignmentUnassignSelected, true);
  };

  return (
    <article className={styles.card}>
      {formattedStartDate && (
        <time
          className={clsx(styles.startDate, 'font-weight-bolder')}
          dateTime={localeStartDate}
        >
          {formattedStartDate}
        </time>
      )}
      <Breadcrumbs
        lessonLink={lessonLink}
        lessonNumber={lessonNumber}
        domain={domain}
        subject={content.subject}
        domainProgramLink={domainProgramLink}
        unitTitle={'unitTitle' in content ? content.unitTitle : undefined}
        unitKey={'unitKey' in content ? content.unitKey : undefined}
        isCustomized={content.brand === Brand.CUSTOMIZED}
        isCustom={content.brand === Brand.CUSTOM}
        isTutorial={isTutorial}
        onDomainBadgeClick={handleDomainBadgeClick}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      <p className={clsx('scale-plus-1', styles.mainText)}>
        <span className="font-weight-bolder">{lessonName}</span>
        {assignmentClassLabel && <span>{` with ${assignmentClassLabel}`}</span>}
      </p>
      {formattedDueDate && (
        <div className={clsx(styles.dueDate, 'scale-minus-1-tight')}>
          {`${t('Due Date')}: `}
          <time dateTime={localeDueDate}>{formattedDueDate}</time>
        </div>
      )}
      <div className="w-100 d-flex justify-content-end align-items-end flex-grow-1">
        <OvalButton
          onClick={handleEditClick}
          onContextMenu={handleEditContextMenu}
          classNames="btn-secondary scale-minus-1"
        >
          {t('Edit')}
        </OvalButton>
        <OvalButton
          onClick={handleUnassignClick}
          onContextMenu={handleUnassignContextMenu}
          classNames="btn-blue scale-minus-1 rhythm-ml-1"
        >
          {t('Unassign')}
        </OvalButton>
      </div>
    </article>
  );
};
